/**
 * Fixed-capacity ring buffers and growable typed arrays.
 *
 * Every high-rate series in the terminal lands in one of these. The point is
 * that ingesting a trade at 500/s must not allocate: a plain array of objects
 * would produce ~30k short-lived objects a minute per symbol and hand the GC
 * a reason to stall a frame. Parallel typed arrays cost one store per field.
 */

/**
 * A trade tape backed by parallel Float64Arrays.
 *
 * Indexing is newest-first: `at(0)` is the most recent trade. That is the
 * order the tape renders in, and it means a renderer never has to know where
 * the physical write head is.
 */
export class TradeRing {
  readonly cap: number;
  /** Physical index of the next write. */
  private head = 0;
  /** Number of valid entries, saturating at cap. */
  size = 0;
  /** Monotonic push counter — renderers diff this to skip idle frames. */
  seq = 0;

  readonly price: Float64Array;
  readonly qty: Float64Array;
  readonly time: Float64Array;
  readonly id: Float64Array;
  /** 1 = aggressive buy (taker lifted the offer), 0 = aggressive sell. */
  readonly side: Uint8Array;

  constructor(cap = 4096) {
    this.cap = cap;
    this.price = new Float64Array(cap);
    this.qty = new Float64Array(cap);
    this.time = new Float64Array(cap);
    this.id = new Float64Array(cap);
    this.side = new Uint8Array(cap);
  }

  push(price: number, qty: number, time: number, side: 0 | 1, id: number): void {
    const h = this.head;
    this.price[h] = price;
    this.qty[h] = qty;
    this.time[h] = time;
    this.side[h] = side;
    this.id[h] = id;
    this.head = h + 1 === this.cap ? 0 : h + 1;
    if (this.size < this.cap) this.size++;
    this.seq++;
  }

  /** Physical index for a newest-first logical index. */
  idx(i: number): number {
    // head points one past the newest entry.
    let p = this.head - 1 - i;
    while (p < 0) p += this.cap;
    return p;
  }

  priceAt(i: number): number {
    return this.price[this.idx(i)];
  }
  qtyAt(i: number): number {
    return this.qty[this.idx(i)];
  }
  timeAt(i: number): number {
    return this.time[this.idx(i)];
  }
  sideAt(i: number): number {
    return this.side[this.idx(i)];
  }
  idAt(i: number): number {
    return this.id[this.idx(i)];
  }

  clear(): void {
    this.head = 0;
    this.size = 0;
    this.seq++;
  }
}

/**
 * A Float64Array that grows geometrically. Klines append forever while the
 * terminal is open; doubling keeps amortised append at O(1) and avoids the
 * per-element boxing a normal array would incur.
 */
export class GrowableF64 {
  data: Float64Array;
  length = 0;

  constructor(initial = 1024) {
    this.data = new Float64Array(initial);
  }

  ensure(n: number): void {
    if (n <= this.data.length) return;
    let cap = this.data.length || 1;
    while (cap < n) cap *= 2;
    const next = new Float64Array(cap);
    next.set(this.data.subarray(0, this.length));
    this.data = next;
  }

  push(v: number): void {
    this.ensure(this.length + 1);
    this.data[this.length++] = v;
  }

  set(i: number, v: number): void {
    this.data[i] = v;
  }

  get(i: number): number {
    return this.data[i];
  }

  get last(): number {
    return this.length > 0 ? this.data[this.length - 1] : NaN;
  }

  clear(): void {
    this.length = 0;
  }

  /** Drop the oldest `n` entries, shifting the rest down. */
  trimFront(n: number): void {
    if (n <= 0) return;
    if (n >= this.length) {
      this.length = 0;
      return;
    }
    this.data.copyWithin(0, n, this.length);
    this.length -= n;
  }

  subarray(): Float64Array {
    return this.data.subarray(0, this.length);
  }
}

/**
 * A tiny fixed-window numeric ring used for rolling statistics — frame times,
 * message latencies, message rates. Keeps a running sum so mean() is O(1).
 */
export class Rolling {
  private buf: Float64Array;
  private i = 0;
  private n = 0;
  private sum = 0;

  constructor(readonly cap: number) {
    this.buf = new Float64Array(cap);
  }

  push(v: number): void {
    if (this.n === this.cap) {
      this.sum -= this.buf[this.i];
    } else {
      this.n++;
    }
    this.buf[this.i] = v;
    this.sum += v;
    this.i = this.i + 1 === this.cap ? 0 : this.i + 1;
  }

  get count(): number {
    return this.n;
  }

  mean(): number {
    return this.n === 0 ? 0 : this.sum / this.n;
  }

  /** Exact percentile via a copy-and-sort. Only called for status readouts. */
  percentile(p: number): number {
    if (this.n === 0) return 0;
    const copy = Array.prototype.slice.call(this.buf, 0, this.n) as number[];
    copy.sort((a, b) => a - b);
    const idx = Math.min(this.n - 1, Math.max(0, Math.round((this.n - 1) * p)));
    return copy[idx];
  }

  max(): number {
    let m = -Infinity;
    for (let k = 0; k < this.n; k++) if (this.buf[k] > m) m = this.buf[k];
    return this.n ? m : 0;
  }

  clear(): void {
    this.i = 0;
    this.n = 0;
    this.sum = 0;
  }
}
